import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { ConnectionState, DisconnectReason, RoomEvent } from 'livekit-client'
import { EventLiveKitProvider, useEventLiveKit } from '@/components/livekit/EventLiveKitProvider'
import { REMOVAL_REFRESH_GAP_MS, retryDelayMs } from '@/lib/watch/reconnect'
import { bufferBroadcastTracks } from '@/lib/watch/broadcastPlayout'
import type { WatchConnectionState } from '@/lib/watch/types'
import { useWatchToken, type WatchTokenError } from '@/lib/watch/useWatchToken'

/** What `WatchRoom` gives its children: this tab's identity, the connection, a failed token fetch, and a way to fetch anew. */
export interface WatchRoomStatus {
  /** This tab's LiveKit identity, or null before the first token. */
  identity: string | null
  /** The watch token this tab joined with, which proves the connection is its own (`useWatchingStage`); null before the first. */
  token: string | null
  /** The room's connection (see `WatchConnectionState`). */
  connection: WatchConnectionState
  /**
   * Why there is no new watch token: `busy` while the API throttles the fetch (429, retrying after its Retry-After),
   * `connect_failed` for a failure it retries, `unavailable` when the event is gone (a 404 `not_found` or a 410), which
   * it doesn't retry; else null.
   */
  error: WatchTokenError | null
  /** Fetch a new watch token now, e.g. when the server says this connection is gone. */
  refresh: () => void
}

/**
 * Keeps the room alive and says how it is. When LiveKit removes this connection (the server revoked it, e.g. after
 * leaving the stage), it fetches a new watch token at once so the viewer reconnects watching; a removal inside the
 * gap after the last such refresh waits for the gap to end, and removals while one waits collapse into it. When a
 * connect fails or the room drops for any other reason (LiveKit gave up reconnecting, the server shut down, LiveKit
 * left the room as the page went), the room is lost: it fetches a new token after about 2 s, 5 s, 15 s, then every
 * 30 s (each spread from half to one and a half times, so a crowd that dropped together comes back apart) until the
 * room connects again, which starts the backoff over. The provider's own disconnect (a token swap) needs nothing.
 *
 * LiveKit leaves the room when the page goes (`pagehide`, including Back into the back-forward cache, and `freeze` in
 * a background tab) and reports it client-initiated, like a token swap; `disconnectIntended` tells them apart. When
 * the page comes back (`pageshow` from the cache, `resume`) or the browser is back online with the room gone, it
 * fetches a new token at once instead of waiting out the backoff, unless a removal's refresh is waiting for its gap.
 */
function useRoomConnection(refresh: () => void): WatchConnectionState {
  const room = useRoomContext()
  const { connectionState, connectFailed, disconnectIntended } = useEventLiveKit()
  const connected = connectionState === ConnectionState.Connected || connectionState === ConnectionState.SignalReconnecting

  const [lost, setLost] = useState(false)
  const [wasConnected, setWasConnected] = useState(false)
  if (connected && !wasConnected) setWasConnected(true)

  const refreshRef = useRef(refresh)
  const disconnectIntendedRef = useRef(disconnectIntended)
  const seenRef = useRef({ lost, wasConnected })
  useEffect(() => {
    refreshRef.current = refresh
    disconnectIntendedRef.current = disconnectIntended
    seenRef.current = { lost, wasConnected }
  })

  // Lost: one refresh at a time, each waiting longer than the last.
  const failuresRef = useRef(0)
  const lostRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const markLost = useCallback(() => {
    setLost(true)
    if (lostRetryRef.current !== null) return
    failuresRef.current += 1
    lostRetryRef.current = setTimeout(() => {
      lostRetryRef.current = null
      refreshRef.current()
    }, retryDelayMs(failuresRef.current, { jitter: 'proportional' }))
  }, [])

  // Connected again (not merely still connected: the room's Disconnected event can arrive a render before the
  // provider's state follows): the room is back and the next loss starts the backoff over.
  useEffect(() => {
    if (!connected) return
    setLost(false)
    failuresRef.current = 0
    if (lostRetryRef.current !== null) clearTimeout(lostRetryRef.current)
    lostRetryRef.current = null
  }, [connected])

  useEffect(() => {
    if (connectFailed) markLost()
  }, [connectFailed, markLost])

  // Removed: at once, at most once per gap.
  const lastRemovalRefreshRef = useRef<number | null>(null)
  const removalWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const refreshAfterRemoval = () => {
      removalWaitRef.current = null
      lastRemovalRefreshRef.current = Date.now()
      refreshRef.current()
    }
    const onRemoved = () => {
      if (removalWaitRef.current !== null) return
      const last = lastRemovalRefreshRef.current
      const wait = last === null ? 0 : last + REMOVAL_REFRESH_GAP_MS - Date.now()
      if (wait > 0) removalWaitRef.current = setTimeout(refreshAfterRemoval, wait)
      else refreshAfterRemoval()
    }
    const onDisconnected = (reason?: DisconnectReason) => {
      if (reason === DisconnectReason.PARTICIPANT_REMOVED) onRemoved()
      else if (reason !== DisconnectReason.CLIENT_INITIATED || !disconnectIntendedRef.current()) markLost()
    }
    room.on(RoomEvent.Disconnected, onDisconnected)
    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected)
      if (removalWaitRef.current !== null) clearTimeout(removalWaitRef.current)
      removalWaitRef.current = null
      if (lostRetryRef.current !== null) clearTimeout(lostRetryRef.current)
      lostRetryRef.current = null
    }
  }, [room, markLost])

  // Back with the room gone: fetch now. Only once it has connected (or is lost), and not while LiveKit still holds or
  // restores the room, or a removal's refresh waits for its gap.
  useEffect(() => {
    const retryNow = () => {
      if (room.state !== ConnectionState.Disconnected || removalWaitRef.current !== null) return
      if (!seenRef.current.lost && !seenRef.current.wasConnected) return
      if (lostRetryRef.current !== null) clearTimeout(lostRetryRef.current)
      lostRetryRef.current = null
      setLost(true)
      refreshRef.current()
    }
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) retryNow()
    }
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('online', retryNow)
    document.addEventListener('resume', retryNow)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('online', retryNow)
      document.removeEventListener('resume', retryNow)
    }
  }, [room])

  if (connected) return 'connected'
  if (lost) return 'lost'
  return connectionState === ConnectionState.Reconnecting || wasConnected ? 'reconnecting' : 'connecting'
}

/** Inside the provider, where the room is: works out the status and hands it to the children. */
function WatchRoomStatusProvider({
  identity,
  token,
  error,
  refresh,
  children,
}: Omit<WatchRoomStatus, 'connection'> & { children: (status: WatchRoomStatus) => ReactNode }) {
  const room = useRoomContext()
  useEffect(() => bufferBroadcastTracks(room), [room])
  const connection = useRoomConnection(refresh)
  return <>{children({ identity, token, connection, error, refresh })}</>
}

/**
 * The watch screen's LiveKit room, joined with the watch token alone (never a viewer token). Until the
 * token arrives the provider has no config, so it keeps an idle room for the hooks and does not connect.
 * The page plays the room's subscribed audio, at the viewer's own volume, once the browser allows it
 * (its `EventLiveKitAudioRenderer`); `LayoutAudioGate` chooses which tracks are subscribed. `children` gets this tab's identity, the connection, the token error and
 * `refresh`. Render it with `key={eventId}`: the provider keeps one room per instance.
 */
export function WatchRoom({ eventId, children }: { eventId: string; children: (status: WatchRoomStatus) => ReactNode }) {
  const { connection, error, refresh } = useWatchToken(eventId)
  const config = useMemo(
    () =>
      connection
        ? {
            eventId,
            token: connection.token,
            serverUrl: connection.serverUrl,
            viewerTelemetry: { surface: 'watch_screen', viewerId: connection.identity, viewerIdentity: connection.identity },
          }
        : null,
    [eventId, connection],
  )

  return (
    <EventLiveKitProvider config={config} renderAudio={false}>
      <WatchRoomStatusProvider identity={connection?.identity ?? null} token={connection?.token ?? null} error={error} refresh={refresh}>
        {children}
      </WatchRoomStatusProvider>
    </EventLiveKitProvider>
  )
}
